import React, { useState, useRef, useEffect, useCallback } from 'react';

interface StatusBarProps {
  state: number;
  onStateChange: (newState: number) => Promise<void>;
}

const states = [
  { label: 'unbehandelt', percent: 0 },
  { label: 'angefangen', percent: 25 },
  { label: 'fortgesetzt', percent: 50 },
  { label: 'perfektioniert', percent: 75 },
  { label: 'prüfungsreif', percent: 100 },
];

const StatusBar: React.FC<StatusBarProps> = ({ state, onStateChange }) => {
  const [internalState, setInternalState] = useState(state);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    setInternalState(state);
  }, [state]);

  const calculateState = useCallback((clientX: number) => {
    if (!containerRef.current) return internalState;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const width = rect.width;
    const percentage = Math.max(0, Math.min(100, (x / width) * 100));
    
    // Map to 5 steps
    const step = Math.round(percentage / 25);
    return Math.min(4, Math.max(0, step));
  }, [internalState]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    const newState = calculateState(e.clientX);
    setInternalState(newState);
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (isDragging) {
      const newState = calculateState(e.clientX);
      setInternalState(newState);
    }
  }, [isDragging, calculateState]);

  const handleMouseUp = useCallback(async (e: MouseEvent) => {
    if (isDragging) {
      setIsDragging(false);
      const newState = calculateState(e.clientX);
      try {
        await onStateChange(newState);
      } catch (err) {
        // Revert UI if update fails
        setInternalState(state);
      }
    }
  }, [isDragging, calculateState, onStateChange, state]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const currentState = states[internalState];

  return (
    <div 
      className="status-bar-container" 
      ref={containerRef}
      onMouseDown={handleMouseDown}
    >
      <div 
        className="status-bar-green" 
        style={{ width: `${currentState.percent}%` }}
      />
      <div className="status-bar-text">
        {currentState.label}
      </div>
    </div>
  );
};

export default StatusBar;
